import { INLINE_LINK } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';
import { LegalPage } from '@/features/legal/LegalPage';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import type { ReactElement } from 'react';

/**
 * Trust Center — security page (`/trust/security`). Phase 6 Pass 3
 * iter 246.
 *
 * Per `pass-3-trust-center.md` § Pages → `/trust/security`:
 * "SOC 2 status, ISO 27001 status, encryption details, IAM model,
 * incident response."
 *
 * **Content authority**: this page is the public projection of
 * `.claude/rules/security.md`. When the rule changes (key rotation,
 * new header, new IAM scope discipline), this page changes in the
 * same PR. Procurement reviewers regularly compare a vendor's
 * stated security posture against their internal audit; keeping
 * source-of-truth and public claim in lockstep prevents future
 * embarrassment.
 *
 * **Marketing voice (Marketer hat, locked)**: every section names
 * specific commitments + numbers (encryption at rest / TLS 1.3 /
 * Auth0 / Vanta / 24-hour breach-notice SLA / SOC 2 Type I Q3 2026 /
 * Q4 2026 Type II / ISO 27001 Q4 2026 / EU residency). Internal
 * implementation detail (cloud-provider service names, framework
 * names, region codes) is deliberately kept off the public surface.
 *
 * **Layout (Designer hat, locked)**: section-per-topic with
 * semantic `<section aria-labelledby>` + `<h2>` so AT users can
 * navigate by heading. Same reading discipline as the hub —
 * ctrl-F-friendly, no decorative visuals.
 *
 * **Three-layer contract preserved**: pure presentational. No
 * data fetch, no state.
 *
 * **i18n**: every user-facing string resolves via `useT()` under the
 * `security.*` namespace. The English version of this legal/trust copy
 * is the legally authoritative one — `<LegalTranslationNotice />` (which
 * self-hides on EN) states that under the `<h1>` for non-EN readers.
 */
export function Security(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('security.meta.title'),
    description: t('security.meta.description'),
    path: '/trust/security',
  });
  return (
    <LegalPage
      breadcrumb={t('security.backToTrust')}
      title={t('security.title')}
      intro={t('security.intro')}
    >
      <section aria-labelledby="sec-production" className="mb-10">
        <h2
          id="sec-production"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('security.production.heading')}
        </h2>
        <p>{t('security.production.body')}</p>
      </section>

      <section aria-labelledby="sec-isolation" className="mb-10">
        <h2
          id="sec-isolation"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('security.isolation.heading')}
        </h2>
        <p className="mb-3">{t('security.isolation.lead')}</p>
        <ol className="list-decimal space-y-1 ps-6">
          <li>{t('security.isolation.layer.boundary')}</li>
          <li>{t('security.isolation.layer.context')}</li>
          <li>{t('security.isolation.layer.queries')}</li>
          <li>{t('security.isolation.layer.rls')}</li>
        </ol>
        <p className="mt-3">{t('security.isolation.evidence')}</p>
      </section>

      <section aria-labelledby="sec-encryption" className="mb-10">
        <h2
          id="sec-encryption"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('security.encryption.heading')}
        </h2>
        <p className="mb-3">{t('security.encryption.rest')}</p>
        <p>{t('security.encryption.transit')}</p>
      </section>

      <section aria-labelledby="sec-identity" className="mb-10">
        <h2
          id="sec-identity"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('security.identity.heading')}
        </h2>
        <p>{t('security.identity.body')}</p>
      </section>

      <section aria-labelledby="sec-authorization" className="mb-10">
        <h2
          id="sec-authorization"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('security.authorization.heading')}
        </h2>
        <p>{t('security.authorization.body')}</p>
      </section>

      <section aria-labelledby="sec-iam" className="mb-10">
        <h2
          id="sec-iam"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('security.iam.heading')}
        </h2>
        <p>{t('security.iam.body')}</p>
      </section>

      <section aria-labelledby="sec-application" className="mb-10">
        <h2
          id="sec-application"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('security.application.heading')}
        </h2>
        <p className="mb-3">{t('security.application.validation')}</p>
        <p className="mb-3">{t('security.application.headersIntro')}</p>
        <ul className="list-disc space-y-1 ps-6">
          <li>{t('security.application.headers.csp')}</li>
          <li>{t('security.application.headers.hsts')}</li>
          <li>{t('security.application.headers.frameOptions')}</li>
          <li>{t('security.application.headers.contentType')}</li>
          <li>{t('security.application.headers.referrer')}</li>
          <li>{t('security.application.headers.permissions')}</li>
        </ul>
        <p className="mt-3">{t('security.application.cors')}</p>
      </section>

      <section aria-labelledby="sec-agentic" className="mb-10">
        <h2
          id="sec-agentic"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('security.agentic.heading')}
        </h2>
        <p className="mb-3">{t('security.agentic.lead')}</p>
        <p className="mb-3">{t('security.agentic.posture')}</p>
        <p className="mb-3">{t('security.agentic.defencesIntro')}</p>
        <ul className="list-disc space-y-1 ps-6">
          <li>{t('security.agentic.defence.firewall')}</li>
          <li>{t('security.agentic.defence.provenance')}</li>
          <li>{t('security.agentic.defence.egress')}</li>
          <li>{t('security.agentic.defence.redteam')}</li>
        </ul>
        <p className="mt-3">
          {t('security.agentic.artifactBeforeEmail')}{' '}
          <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
            admin@syncanix.com
          </a>{' '}
          {t('security.agentic.artifactAfterEmail')}
        </p>
      </section>

      <section aria-labelledby="sec-secrets" className="mb-10">
        <h2
          id="sec-secrets"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('security.secrets.heading')}
        </h2>
        <p>{t('security.secrets.body')}</p>
      </section>

      <section aria-labelledby="sec-logging" className="mb-10">
        <h2
          id="sec-logging"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('security.logging.heading')}
        </h2>
        <p>{t('security.logging.body')}</p>
      </section>

      <section aria-labelledby="sec-dependencies" className="mb-10">
        <h2
          id="sec-dependencies"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('security.dependencies.heading')}
        </h2>
        <p>{t('security.dependencies.body')}</p>
      </section>

      <section aria-labelledby="sec-compliance" className="mb-10">
        <h2
          id="sec-compliance"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('security.compliance.heading')}
        </h2>
        <p>
          {t('security.compliance.body')}{' '}
          <Link className={INLINE_LINK} to="/trust/compliance">
            {t('security.compliance.link')}
          </Link>
        </p>
      </section>

      <section aria-labelledby="sec-incident" className="mb-10">
        <h2
          id="sec-incident"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('security.incident.heading')}
        </h2>
        <p>
          {t('security.incident.bodyBeforeEmail')}{' '}
          <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
            admin@syncanix.com
          </a>{' '}
          {t('security.incident.bodyAfterEmail')}
        </p>
      </section>

      <section aria-labelledby="sec-contact">
        <h2
          id="sec-contact"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('security.contact.heading')}
        </h2>
        <ul className="list-disc space-y-1 ps-6">
          <li>
            {t('security.contact.disclosure')}{' '}
            <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
              admin@syncanix.com
            </a>
            {' · '}
            <Link className={INLINE_LINK} to="/trust/disclosure">
              {t('security.contact.disclosurePolicy')}
            </Link>
            .
          </li>
          <li>
            {t('security.contact.procurement')}{' '}
            <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
              admin@syncanix.com
            </a>
            .
          </li>
        </ul>
      </section>
    </LegalPage>
  );
}
